/**
 * CSV export of the visible shift plan (#2038): GET /api/v1/shifts/export.csv, downloaded
 * as dienstplan_<from>_<to>.csv. Same fetch-and-save pattern as the subsidy-report export.
 */
import { useMutation } from '@tanstack/react-query';
import { useLingui } from '@lingui/react';
import { msg } from '@lingui/core/macro';
import { toast } from 'sonner';
import { errorToastText } from '@/lib/translateBackendError';

export interface ShiftExportParams {
  fromDate: string;
  toDate: string;
  childcareId?: string | null;
  groupId?: string | null;
}

type ExportError = Error & { status?: number; body?: unknown };

export async function downloadShiftPlanExport(params: ShiftExportParams): Promise<{ blob: Blob; filename: string }> {
  const { getAuthToken } = await import('@/api/auth-helper');
  const token = await getAuthToken();
  const baseUrl = import.meta.env.VITE_API_BASE || '';
  const query = new URLSearchParams({ from: params.fromDate, to: params.toDate });
  if (params.childcareId) query.set('childcare_id', params.childcareId);
  if (params.groupId) query.set('group_id', params.groupId);

  const response = await fetch(`${baseUrl}/api/v1/shifts/export.csv?${query.toString()}`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!response.ok) {
    const body = await response.json().catch(() => ({}));
    throw Object.assign(new Error(`HTTP ${response.status}`), { status: response.status, body }) as ExportError;
  }
  const blob = await response.blob();
  const disposition = response.headers.get('Content-Disposition');
  const filenameMatch = disposition?.match(/filename="?([^"]+)"?/);
  return { blob, filename: filenameMatch?.[1] || `dienstplan_${params.fromDate}_${params.toDate}.csv` };
}

export function useDownloadShiftPlanExport() {
  const { i18n } = useLingui();
  return useMutation<{ blob: Blob; filename: string }, ExportError, ShiftExportParams>({
    mutationFn: downloadShiftPlanExport,
    onSuccess: ({ blob, filename }) => {
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    },
    onError: (error) => {
      toast.error(
        errorToastText(error, i18n, msg({ id: 'shifts.export.error', message: 'The shift plan could not be exported' }))
      );
    },
  });
}
